import { z } from "zod";

// Model form schema
export const modelSchema = z.object({
  name: z.string().min(1, "Название модели обязательно"),
  brandId: z.string().min(1, "Выберите бренд"),
  description: z.string().optional(),
});

export type ModelFormData = z.infer<typeof modelSchema>;

// FAQ form schema
export const faqSchema = z.object({
  question: z.string().min(1, "Вопрос обязателен"),
  answer: z.string().min(1, "Ответ обязателен"),
});

export type FaqFormData = z.infer<typeof faqSchema>;

// Brand form schema
export const brandSchema = z.object({
  name: z.string().min(1, "Название бренда обязательно"),
  logo: z.string().optional(),
  description: z.string().optional(),
});

export type BrandFormData = z.infer<typeof brandSchema>;

// Review form schema
export const reviewSchema = z.object({
  customerName: z.string().min(1, "Имя клиента обязательно"),
  customerEmail: z.string().email("Неверный email").optional(),
  rating: z.number().min(1).max(5, "Рейтинг должен быть от 1 до 5"),
  comment: z.string().min(1, "Комментарий обязателен"),
  reviewDate: z.string().min(1, "Дата отзыва обязательна"),
  source: z.string().min(1, "Источник обязателен"),
  sourceUrl: z.string().url("Неверный URL").optional(),
  carId: z.string().optional(),
  isApproved: z.boolean().optional().default(false),
  isPublished: z.boolean().optional().default(false),
});

export type ReviewFormData = z.infer<typeof reviewSchema>;

// Video Review form schema
export const videoReviewSchema = z.object({
  customerName: z.string().min(1, "Имя клиента обязательно"),
  customerEmail: z.string().email("Неверный email").optional(),
  videoUrl: z.string().url("Неверный URL видео"),
  thumbnailUrl: z.string().url("Неверный URL превью").optional(),
  title: z.string().min(1, "Заголовок обязателен"),
  description: z.string().optional(),
  reviewDate: z.string().min(1, "Дата отзыва обязательна"),
  customerRole: z.string().min(1, "Роль клиента обязательна"),
  carId: z.string().optional(),
  isApproved: z.boolean().default(false),
  isPublished: z.boolean().default(false),
});

export type VideoReviewFormData = z.infer<typeof videoReviewSchema>;

// Branch form schema
export const branchSchema = z.object({
  name: z
    .string()
    .min(1, "Название филиала обязательно")
    .max(100, "Название не может превышать 100 символов"),
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/i, "Слаг может содержать только буквы, цифры и дефисы")
    .optional()
    .or(z.literal("")),
  arendaTitle: z
    .string()
    .max(200, "Заголовок не может превышать 200 символов")
    .optional()
    .or(z.literal("")),
  podZakazTitle: z
    .string()
    .max(200, "Заголовок не может превышать 200 символов")
    .optional()
    .or(z.literal("")),
  contactTitle: z
    .string()
    .max(200, "Заголовок не может превышать 200 символов")
    .optional()
    .or(z.literal("")),
  description: z
    .string()
    .max(2000, "Описание не может превышать 2000 символов")
    .optional(),
  address: z
    .string()
    .max(300, "Адрес не может превышать 300 символов")
    .optional(),
  city: z.string().max(100, "Город не может превышать 100 символов").optional(),
  state: z
    .string()
    .max(100, "Регион не может превышать 100 символов")
    .optional(),
  country: z
    .string()
    .max(100, "Страна не может превышать 100 символов")
    .optional(),
  postalCode: z
    .string()
    .max(20, "Почтовый индекс не может превышать 20 символов")
    .optional(),
  phone: z
    .string()
    .max(20, "Телефон не может превышать 20 символов")
    .optional(),
  email: z
    .string()
    .email("Неверный формат email")
    .max(100, "Email не может превышать 100 символов")
    .optional()
    .or(z.literal("")),
  website: z
    .string()
    .url("Неверный формат URL")
    .max(200, "URL не может превышать 200 символов")
    .optional()
    .or(z.literal("")),
  latitude: z
    .union([z.string(), z.number(), z.null()])
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (val === "" || val === null || val === undefined) return true;
        const num = typeof val === "string" ? parseFloat(val) : val;
        return !isNaN(num) && num >= -90 && num <= 90;
      },
      {
        message: "Широта должна быть числом от -90 до 90",
      }
    )
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      return typeof val === "string" ? parseFloat(val) : val;
    }),
  longitude: z
    .union([z.string(), z.number(), z.null()])
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (val === "" || val === null || val === undefined) return true;
        const num = typeof val === "string" ? parseFloat(val) : val;
        return !isNaN(num) && num >= -180 && num <= 180;
      },
      {
        message: "Долгота должна быть числом от -180 до 180",
      }
    )
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      return typeof val === "string" ? parseFloat(val) : val;
    }),
  isActive: z.boolean(),
  isMainBranch: z.boolean(),
  timezone: z
    .string()
    .max(50, "Часовой пояс не может превышать 50 символов")
    .optional(),
  currency: z
    .string()
    .max(10, "Валюта не может превышать 10 символов")
    .optional(),
  metaTitle: z
    .string()
    .max(60, "SEO заголовок не может превышать 60 символов")
    .optional(),
  metaDescription: z
    .string()
    .max(160, "SEO описание не может превышать 160 символов")
    .optional(),
  metaKeywords: z
    .string()
    .max(255, "SEO ключевые слова не могут превышать 255 символов")
    .optional(),
  crmUrl: z
    .string()
    .max(500, "CRM URL не может превышать 500 символов")
    .optional()
    .or(z.literal("")),
  crmToken: z
    .string()
    .max(2000, "CRM токен не может превышать 2000 символов")
    .optional()
    .or(z.literal("")),
  avito_link: z
    .string()
    .url("Неверный формат URL")
    .max(500, "URL не может превышать 500 символов")
    .optional()
    .or(z.literal("")),
  yandex_link: z
    .string()
    .url("Неверный формат URL")
    .max(500, "URL не может превышать 500 символов")
    .optional()
    .or(z.literal("")),
  telegram_link: z
    .string()
    .url("Неверный формат URL")
    .max(500, "URL не может превышать 500 символов")
    .optional()
    .or(z.literal("")),
});

export type BranchFormData = z.input<typeof branchSchema>;

// Rental configuration schema
export const rentalConfigSchema = z
  .object({
    status: z.enum(["RENTING", "ACTIVE", "ARCHIVED"], {
      message: "Выберите статус аренды",
    }),
    rentalPeriod: z.enum(["DAY", "HOUR", "WEEK", "MONTH"], {
      message: "Выберите период аренды",
    }),
    price: z.number().min(0, "Цена должна быть положительной"),
    hasDiscount: z.boolean().default(false),
    discountType: z.enum(["HOURS", "DAYS"]).optional(),
    discountDuration: z.number().min(0).default(0),
    discountCreatedAt: z.string().nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.hasDiscount && !data.discountType) {
        return false;
      }
      return true;
    },
    {
      message: "Тип скидки обязателен при наличии скидки",
      path: ["discountType"],
    }
  );

// Meta information schema
export const productMetaSchema = z.object({
  title: z.string().min(1, "SEO заголовок обязателен"),
  description: z.string().min(1, "SEO описание обязательно"),
  keywords: z.string().min(1, "SEO ключевые слова обязательны"),
});

// Main product form schema matching the new structure
export const productSchema = z.object({
  lotNumber: z.string().optional(),
  brand: z.string().min(1, "Бренд обязателен"),
  model: z.string().min(1, "Модель обязательна"),
  brandId: z.string().optional(),
  modelId: z.string().optional(),
  year: z
    .number()
    .min(1900, "Год должен быть не менее 1900")
    .max(new Date().getFullYear() + 1, "Год не может быть в будущем"),
  mileage: z.number().min(0, "Пробег не может быть отрицательным"),
  fuelType: z.enum(["GASOLINE", "DIESEL", "ELECTRIC", "HYBRID", "LPG"], {
    message: "Выберите тип топлива",
  }),
  transmission: z.enum(["MANUAL", "AUTOMATIC", "CVT", "SEMI_AUTOMATIC"], {
    message: "Выберите тип трансмиссии",
  }),
  drivetrain: z.enum(["FWD", "RWD", "AWD", "4WD"], {
    message: "Выберите тип привода",
  }),
  engineSize: z.number().min(0.1, "Объем двигателя должен быть больше 0"),
  licensePlate: z.string().min(1, "Госномер обязателен"),
  color: z.string().min(1, "Цвет обязателен"),
  location: z.string().optional(),
  status: z.enum(["ACTIVE", "RENTING", "ARCHIVED"], {
    message: "Выберите статус автомобиля",
  }),
  // Flattened rental fields instead of nested rental object
  rentalStatus: z.enum(["RENTING", "ACTIVE", "ARCHIVED"], {
    message: "Выберите статус аренды",
  }),
  rentalPeriod: z.enum(["DAY", "HOUR", "WEEK", "MONTH"], {
    message: "Выберите период аренды",
  }),
  price: z.number().min(0, "Цена должна быть положительной"),
  hasDiscount: z.boolean().default(false),
  discountType: z.enum(["HOURS", "DAYS"]).optional(),
  discountDuration: z.number().min(0).default(0),
  discountAmount: z.number().min(0).default(0),
  discountCreatedAt: z.string().nullable().optional(),
  // Media fields
  images: z.array(z.string()).optional().default([]),
  mainImage: z.string().optional().default(""),
  description: z.string().optional().default(""),
  order: z.number().min(0, "Порядок не может быть отрицательным").default(0),
  meta: productMetaSchema.optional(),
  isActive: z.boolean().default(true),
  isFeatured: z.boolean().optional().default(false),
});

export type ProductFormData = z.infer<typeof productSchema>;

// User form schema
export const userSchema = z
  .object({
    email: z
      .string()
      .email("Неверный формат email")
      .max(100, "Email не может превышать 100 символов")
      .optional()
      .or(z.literal("")),
    password: z
      .string()
      .min(6, "Пароль должен содержать минимум 6 символов")
      .max(100, "Пароль не может превышать 100 символов")
      .optional()
      .or(z.literal("")),
    role: z.enum(["ADMIN", "MULTI_BRANCH_ADMIN", "REGULAR", "SUPER_ADMIN", "DEVELOPER"], {
      message: "Выберите роль пользователя",
    }),
    branchId: z.string().optional().or(z.literal("")),
    telegramFirstName: z
      .string()
      .max(100, "Имя в Telegram не может превышать 100 символов")
      .optional()
      .or(z.literal("")),
    telegramLastName: z
      .string()
      .max(100, "Фамилия в Telegram не может превышать 100 символов")
      .optional()
      .or(z.literal("")),
    confirmPassword: z.string().optional().or(z.literal("")),
  })
  .refine(
    (data) => {
      // If password is provided, confirmPassword must match
      if (data.password && data.password.length > 0) {
        return data.password === data.confirmPassword;
      }
      return true;
    },
    {
      message: "Пароли не совпадают",
      path: ["confirmPassword"],
    }
  );

export type UserFormData = z.infer<typeof userSchema>;
